import React, { useEffect, useState } from "react";
import { Alert, Box, Grid, Skeleton, Typography } from "@mui/material";
import QuickActionsRow from "./components/QuickActionsRow";
import KpiCard from "./components/KpiCard";
import Panel from "./components/Panel";
import RevenueChart from "./components/RevenueChart";
import StatusDonut from "./components/StatusDonut";
import ProductionPipeline from "./components/ProductionPipeline";
import AwaitingActionList from "./components/AwaitingActionList";
import TopOrganizationsTable from "./components/TopOrganizationsTable";
import RecentActivityTable from "./components/RecentActivityTable";
import { dashboardService } from "../../../services/dashboard.service";
import { formatCurrencyShort } from "./formatters";

const buildKpiCards = (kpis) => {
  const trendDelta = (pct, fallback) =>
    pct === null || pct === undefined ? fallback : `${Math.abs(pct)}% vs prior ${kpis.windowDays} days`;

  return [
    {
      label: "Open quotations",
      value: `${kpis.openQuotations}`,
      delta: trendDelta(kpis.openQuotationsTrendPercent, "No new quotations in prior period"),
      direction: (kpis.openQuotationsTrendPercent ?? 0) < 0 ? "down" : "up",
      accent: "#6d2f9c",
    },
    {
      label: `Sales orders (last ${kpis.windowDays} days)`,
      value: `${kpis.salesOrdersCount} · ${formatCurrencyShort(kpis.salesOrdersRevenue)}`,
      delta: trendDelta(kpis.salesOrdersTrendPercent, "No sales orders in prior period"),
      direction: (kpis.salesOrdersTrendPercent ?? 0) < 0 ? "down" : "up",
      accent: "#0f6e56",
    },
    {
      label: "Pending approvals",
      value: `${kpis.pendingApprovals}`,
      delta: `${kpis.pendingApprovalsOverdue} over 14 days old`,
      direction: kpis.pendingApprovalsOverdue > 0 ? "warn" : "up",
      accent: "#b5790f",
    },
    {
      label: "Unpaid invoices",
      value: formatCurrencyShort(kpis.unpaidInvoices),
      delta: `${kpis.unpaidInvoicesOverdue} over 14 days old`,
      direction: kpis.unpaidInvoicesOverdue > 0 ? "down" : "up",
      accent: "#d85a30",
    },
  ];
};

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    dashboardService
      .getSummary()
      .then((res) => {
        if (!cancelled) setSummary(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.toString?.() || "Failed to load dashboard data");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 22 }}>Dashboard</Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
          Overview of quotations, production and billing
        </Typography>
      </Box>

      <QuickActionsRow />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!summary && !error && (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
      )}

      {summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 0.5 }}>
            {buildKpiCards(summary.kpis).map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <KpiCard {...card} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Panel title="Revenue & quotation value, last 6 months">
                <RevenueChart
                  labels={summary.revenueChart.labels}
                  salesOrderRevenue={summary.revenueChart.salesOrderRevenue}
                  quotationValue={summary.revenueChart.quotationValue}
                />
              </Panel>
            </Grid>
            <Grid item xs={12} md={5}>
              <Panel title="Quotation status split">
                <StatusDonut labels={summary.statusDonut.labels} data={summary.statusDonut.data} />
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Panel title="Production pipeline">
                <ProductionPipeline rows={summary.productionPipeline} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={5}>
              <Panel title="Awaiting your action">
                <AwaitingActionList items={summary.awaitingActions} />
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Panel title={`Top organizations, last ${summary.kpis.windowDays} days`}>
                <TopOrganizationsTable organizations={summary.topOrganizations} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel title="Recent activity">
                <RecentActivityTable items={summary.recentActivity} />
              </Panel>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AdminDashboard;
